import React, { useMemo } from 'react';
import clsx from 'clsx';
import type { KitFontId, ReleaseThemeId } from 'fym-shared/src/music24/releaseThemes';
import type { AssetStyleId, WaveformShape } from 'fym-shared/src/music24/assetStyles';
import { TypedMemo } from '../../../common/types';
import { themeStillVars } from './themeStillVars';
/* eslint-disable css-modules/no-unused-class -- the style classes are picked by id at run time */
import styles from './StyleLoopPreview.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * The loop styles that this preview draws. The image styles use `ThemeStill`; `typeOnly` is here
 * too for the countdown card of Studio step 2 (design q3a: large words on the ground).
 */
export type LoopStyleId = Extract<
  AssetStyleId,
  'drift' | 'pulse' | 'liquid' | 'waveform' | 'particles' | 'aiFootage' | 'typeOnly'
>;

const STYLE_CLASS: Readonly<Record<LoopStyleId, string | undefined>> = {
  drift: styles.drift,
  pulse: styles.pulse,
  liquid: styles.liquid,
  waveform: styles.wave,
  particles: styles.particles,
  aiFootage: styles.ai,
  typeOnly: styles.type,
};

const SHAPE_CLASS: Readonly<Record<WaveformShape, string | undefined>> = {
  bars: styles.bars,
  line: styles.line,
  ring: styles.ring,
  none: undefined,
};

/** The texture of the theme on a loop (design `loop`: `lp-tex` + the texture of the theme). */
function textureClass(themeId: ReleaseThemeId): string | undefined {
  switch (themeId) {
    case 'dark':
    case 'retro':
    case 'warm':
      return styles.grain;
    case 'handmade':
      return styles.paper;
    case 'dreamy':
      return styles.glow;
    case 'clean':
    case 'hype':
    case 'neon':
      return undefined;
  }
}

const BAR_KEYS = ['b1', 'b2', 'b3', 'b4', 'b5', 'b6', 'b7'];

export interface StyleLoopPreviewProps {
  style: LoopStyleId;
  /** The shape of the Waveform style. */
  waveform?: WaveformShape | null;
  themeId: ReleaseThemeId;
  fontOverride: KitFontId | null;
  palette: readonly string[];
  artworkUrl: string | undefined;
  /** The stand-in motion runs (the card is selected or hovered). */
  playing: boolean;
  /** A short text in the display type of the theme (design `lp-t`, the "3 days" of the countdown card). */
  text?: string;
  className?: string;
}

/**
 * The preview of one loop style in the song's theme (design q3b, q3d): the same ground, colours and
 * texture for every style; only the moving part differs. A loop has no text (the Canvas has none).
 */
const StyleLoopPreviewComponent: React.FC<StyleLoopPreviewProps> = ({
  style,
  waveform,
  themeId,
  fontOverride,
  palette,
  artworkUrl,
  playing,
  text,
  className,
}) => {
  const vars = useMemo(
    () => themeStillVars({ themeId, fontOverride, palette, artworkUrl, title: '', layout: 'card' }),
    [artworkUrl, fontOverride, palette, themeId]
  );
  return (
    <div
      className={clsx(
        styles.lp,
        STYLE_CLASS[style],
        style === 'waveform' && SHAPE_CLASS[waveform ?? 'bars'],
        themeId === 'dark' && styles.grey,
        playing && styles.playing,
        className
      )}
      style={vars}
      aria-hidden="true"
      data-style={style}
    >
      <i className={styles.bg} />
      <i className={styles.c} />
      <span className={styles.w}>
        {BAR_KEYS.map((key) => (
          <i key={key} />
        ))}
      </span>
      {text !== undefined && <span className={styles.t}>{text}</span>}
      <i className={clsx(styles.tex, textureClass(themeId))} />
    </div>
  );
};

export const StyleLoopPreview = TypedMemo(StyleLoopPreviewComponent);
